import React, { useState, useContext, useEffect, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux';
import { Box } from '@mui/joy';
import { Button } from 'src/mtska-frontend-app-component';
import { useTranslation } from 'react-i18next';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import List from './List';
import { AppContext, useSettingContextInComponent, useSettingsContext } from 'src/mtska-frontend-app-scaffolding';
import FleetManagerCarKPI from './FleetManagerCarKPI';
import RentalManagerCarKPI from './RentalManagerCarKPI';
import useFiltersFromLocation from 'src/mtska-frontend-app-scaffolding/hooks/useFiltersFromLocation';
import { DashboardFilterRenderer } from 'src/mtska-frontend-modules/modules/_shared/dashboardFilters/filterRegistry';

const dataProviderName = 'cars/car';

// Inline car-page filter configuration: same config-driven approach used in the dashboard,
// but kept local to this view instead of living in a dedicated JSON file.
const CAR_FILTER_SET = [
    'fleet_status',
    'fleet_area_network',
    'fleet_shop',
    'fleet_owner_account',
    // 'fleet_commercial',
    'fleet_contract_end',
    'fleet_customer_cluster',
    'fleet_customer',
    'fleet_renter',
    'fleet_trace_km',
    // 'fleet_plate',
];

const CAR_FILTER_CATALOG = {
    fleet_status: {
        type: 'staticMultiCheckbox',
        multi: true,
        field: 'status',
        operator: 'in',
        valueKey: 'value',
        labelkey: 'label',
        labelKey: 'Stato Vetture',
        staticOptions: [
            { value: 'running', labelKey: 'running' },
            { value: 'in_order', labelKey: 'in_order' },
            { value: 'closed', labelKey: 'closed' },
            { value: 'canceled', labelKey: 'canceled' },
        ],
    },
    fleet_area_network: {
        type: 'serverMultiCheckbox',
        multi: true,
        field: '_related_account_area_name',
        operator: 'in',
        dataProvider: 'commercials/area',
        valueKey: 'name',
        labelkey: 'name',
        project: ['name'],
        serverFilters: {
            items: [
                {
                    field: 'name',
                    operator: 'in',
                    value: ['DIREZIONE', 'RETE DIRETTA', 'RETE INDIRETTA'],
                },
            ],
        },
        labelKey: 'Rete Commerciale',
        excludedRoles: ['Area manager', 'Account'],
    },
    fleet_shop: {
        type: 'serverMultiCheckbox',
        multi: true,
        field: '_related_account_shop_name',
        operator: 'in',
        dataProvider: 'commercials/shop',
        valueKey: 'name',
        labelkey: 'name',
        project: ['name'],
        restrictByField: {
            sourceField: '_related_account_area_name',
            targetField: '_related_area_name',
            operator: 'in',
        },
        labelKey: 'Area Commerciale',
        excludedRoles: ['Area manager', 'Account'],
    },
    fleet_owner_account: {
        type: 'serverMultiCheckbox',
        multi: true,
        field: '_related_account_name',
        operator: 'in',
        labelKey: 'Azienda proprietario',
        dataProvider: 'persons/employment',
        valueKey: '_related_person_fullname',
        labelkey: '_related_person_fullname',
        project: ['_related_person_fullname'],
        excludedRoles: ['Account'],
        serverFilters: {
            items: [
                {
                    field: 'role',
                    operator: 'in',
                    value: ['Dipendente', 'Filiale', 'Agenzia NLT'],
                },
            ],
        },
        restrictByField: {
            sourceField: '_related_account_shop_name',
            targetField: '_related_shops_readable',
            operator: 'contains',
        },
    },
    fleet_commercial: {
        type: 'inputAutocomplete',
        field: '_related_contract_account_name',
        labelKey: 'Commerciale',
        searchKey: '_related_contract_account_name',
        labelKeyField: '_related_contract_account_name',
        project: ['_related_contract_account_name'],
        distinct: '_related_contract_account_name',
    },
    fleet_contract_end: {
        type: 'staticMultiCheckbox',
        multi: false,
        field: '_related_contract_end_at',
        operator: 'between',
        valueKey: 'value',
        labelkey: 'label',
        labelKey: 'Stato contratti',
        staticOptions: [
            {
                labelKey: 'Scaduti',
                valueDynamic: {
                    type: 'relativeDate',
                    months: 0,
                },
                operator: 'before',
            },
            {
                labelKey: 'expiring within {{months}} months',
                labelKeyParams: { months: 3 },
                valueDynamic: {
                    type: 'relativeDateRange',
                    fromMonths: 0,
                    toMonths: 3,
                },
            },
            {
                labelKey: 'expiring within {{months_start}} and {{months_end}} months',
                labelKeyParams: {
                    months_start: 3,
                    months_end: 6,
                },
                valueDynamic: {
                    type: 'relativeDateRange',
                    fromMonths: 3,
                    toMonths: 6,
                },
            },
            {
                labelKey: 'expiring within {{months_start}} and {{months_end}} months',
                labelKeyParams: {
                    months_start: 6,
                    months_end: 12,
                },
                valueDynamic: {
                    type: 'relativeDateRange',
                    fromMonths: 6,
                    toMonths: 12,
                },
            },
            {
                labelKey: "In scadenza oltre l'anno",
                valueDynamic: {
                    type: 'relativeDateRange',
                    fromMonths: 12,
                    toMonths: 120,
                },
            },
        ],
    },
    fleet_customer_cluster: {
        type: 'staticMultiCheckbox',
        multi: true,
        field: '_related_legalEntity_commercialCluster',
        operator: 'in',
        valueKey: 'value',
        labelkey: 'label',
        labelKey: 'Profilazione Cliente',
        staticOptions: [
            { value: 'Da definire', label: 'Da definire' },
            { value: 'SINGLE (1)', label: 'SINGLE (1)' },
            { value: 'SMALL (2-5)', label: 'SMALL (2-5)' },
            { value: 'MEDIUM (6-20)', label: 'MEDIUM (6-20)' },
            { value: 'TOP (21-50)', label: 'TOP (21-50)' },
            { value: 'VIP (OVER 50)', label: 'VIP (OVER 50)' },
        ],
    },
    fleet_customer: {
        type: 'inputAutocomplete',
        field: '_related_legalEntity_name',
        labelKey: 'customer',
        searchKey: '_related_legalEntity_name',
        labelKeyField: '_related_legalEntity_name',
        project: ['_related_legalEntity_name'],
        distinct: '_related_legalEntity_name',
    },
    fleet_renter: {
        type: 'serverMultiCheckbox',
        multi: false,
        field: '_related_renter_name',
        operator: 'equals',
        labelKey: 'renter',
        dataProvider: 'cars/car/autocomplete',
        valueKey: '_related_renter_name',
        labelkey: '_related_renter_name',
        project: ['_related_renter_name'],
        distinct: '_related_renter_name',
    },
    fleet_trace_km: {
        type: 'staticMultiCheckbox',
        multi: false,
        field: 'projectedDeltaMileage',
        operator: 'betweenNumbers',
        valueKey: 'value',
        labelkey: 'label',
        labelKey: 'Rilevamento Km',
        staticOptions: [
            {
                value: { from: -1000, to: 1000 },
                label: 'In linea',
            },
            {
                value: { from: -1000000, to: -1001 },
                label: 'In difetto',
            },
            {
                value: { from: 1001, to: 1000000 },
                label: 'In eccesso',
            },
        ],
    },
    fleet_plate: {
        type: 'inputAutocomplete',
        field: 'plate',
        labelKey: 'plate',
        searchKey: 'plate',
        labelKeyField: 'plate',
        project: ['plate'],
    },
};

const LocalFilter = ({ filterModel, addFilter, onClearFilters, deferHydration = false }) => {
    const { t } = useTranslation();
    const { item: user } = useDataProvider('auth/token');
    const hasFilters = (filterModel?.items ?? []).length > 0;

    return (
        <Box className="dashboard-page-title-filters" sx={{ marginBottom: '40px' }}>
            {CAR_FILTER_SET.map((filterId) => (
                <DashboardFilterRenderer
                    key={filterId}
                    filterId={filterId}
                    t={t}
                    filterModel={filterModel}
                    addFilter={addFilter}
                    filterCatalog={CAR_FILTER_CATALOG}
                    visibilityUser={user}
                    deferHydration={deferHydration}
                />
            ))}
            <Box className="dashboard-page-title-filters-actions">
                <Button
                    onClick={onClearFilters}
                    className="reset-filters"
                    size="tiny"
                    icon="faTrash"
                    title={t('Cancella filtri')}
                    disabled={!hasFilters}
                />
            </Box>
        </Box>
    );
};

const Default = () => {
    const { appConfiguration, userCapabilities } = useContext(AppContext);
    const [filtersFromLocation] = useFiltersFromLocation();
    const [locationFiltersApplied, setLocationFiltersApplied] = useState(false);
    const bootstrapDefaultFilters = locationFiltersApplied ? undefined : filtersFromLocation;
    const { filterModel } = useSettingContextInComponent(dataProviderName, bootstrapDefaultFilters);
    const { settingsLoaded, addFilter, addFilters, emptyFilters } = useSettingsContext(dataProviderName);
    const carListProviderState = useSelector((state) => state[dataProviderName]);
    const listLoading = useMemo(
        () => Object.values(carListProviderState?.components ?? {}).some((component) => component?.loading),
        [carListProviderState],
    );
    const listHasData = useMemo(
        () => Object.values(carListProviderState?.components ?? {}).some(
            (component) => (Array.isArray(component?.items) && component.items.length > 0) || (component?.total ?? 0) > 0,
        ),
        [carListProviderState],
    );
    const [secondaryHydrationReady, setSecondaryHydrationReady] = useState(false);
    const listHydrationObservedRef = useRef(false);

    useEffect(() => {
        if (!settingsLoaded || locationFiltersApplied) return;

        const locationItems = filtersFromLocation?.items ?? [];
        if (locationItems.length > 0) {
            addFilters(locationItems);
        }

        setLocationFiltersApplied(true);
    }, [settingsLoaded, locationFiltersApplied, filtersFromLocation, addFilters]);

    useEffect(() => {
        if (secondaryHydrationReady) return;

        if (listLoading) {
            listHydrationObservedRef.current = true;
            setSecondaryHydrationReady(true);
            return;
        }

        if (listHydrationObservedRef.current || listHasData) {
            setSecondaryHydrationReady(true);
        }
    }, [listLoading, listHasData, secondaryHydrationReady]);

    useEffect(() => {
        const fallbackTimer = setTimeout(() => setSecondaryHydrationReady(true), 5000);
        return () => clearTimeout(fallbackTimer);
    }, []);

    const deferSecondaryHydration = !secondaryHydrationReady;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column-reverse' }}>
            <List filters={bootstrapDefaultFilters} />
            <Box>
                {appConfiguration.dashboard.view == 'fleetmanager' && (
                    <FleetManagerCarKPI
                        filters={filterModel}
                        userCapabilities={userCapabilities}
                        deferLoad={deferSecondaryHydration}
                    />
                )}
                {appConfiguration.dashboard.view == 'ConsecutionSales' && (
                    <>
                        <LocalFilter
                            filterModel={filterModel}
                            addFilter={addFilter}
                            onClearFilters={emptyFilters}
                            deferHydration={deferSecondaryHydration}
                        />
                        <RentalManagerCarKPI
                            filters={filterModel}
                            userCapabilities={userCapabilities}
                            deferLoad={deferSecondaryHydration}
                        />
                    </>
                )}
            </Box>
        </Box>
    );
}

export default Default;


